import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from "@/components/ui/tooltip";
import { Skeleton } from "@/components/ui/skeleton";
import { formatCurrencyWithCode, type CurrencyCode } from "@forgepoint/shared";
import Decimal from "decimal.js";

interface LineMarginCellProps {
  margin: { percent: string; profitPerUnit: string } | null;
  quantity: string | undefined;
  baseCurrencyCode?: CurrencyCode;
  isBom: boolean;
  shouldShowSkeleton: boolean;
}

/**
 * Renders a quote line's read-only margin %, with the base-currency profit per
 * unit and line profit on hover. BOM items show a blended margin, flagged with
 * an asterisk and explained in the tooltip.
 */
export function LineMarginCell({
  margin,
  quantity,
  baseCurrencyCode,
  isBom,
  shouldShowSkeleton,
}: LineMarginCellProps) {
  if (shouldShowSkeleton) {
    return (
      <div className="flex justify-end">
        <Skeleton className="h-4 w-8 sm:h-5 sm:w-12" />
      </div>
    );
  }

  if (!margin || !quantity) {
    return <span className="text-xs sm:text-sm text-muted-foreground">-</span>;
  }

  const currency = baseCurrencyCode ?? "USD";
  const lineProfit = new Decimal(margin.profitPerUnit)
    .times(quantity)
    .toString();

  return (
    <TooltipProvider>
      <Tooltip>
        <TooltipTrigger asChild>
          <span className="cursor-help text-xs sm:text-sm tabular-nums">
            {isBom ? `${margin.percent}%*` : `${margin.percent}%`}
          </span>
        </TooltipTrigger>
        <TooltipContent>
          <p>{`Profit per unit: ${formatCurrencyWithCode(margin.profitPerUnit, currency)}`}</p>
          <p>{`Line profit: ${formatCurrencyWithCode(lineProfit, currency)}`}</p>
          {isBom && (
            <p>Blended BOM margin. See Review item for the breakdown.</p>
          )}
        </TooltipContent>
      </Tooltip>
    </TooltipProvider>
  );
}
